import './dasharray.less';
import { isNull, isUndefined } from 'lodash';
import React from 'react';
import { Select } from '@meitu/saas-ui';
import { BRUSH_DASHARRAY_OPTIONS } from '@meitu/saas-editor-core/es/layers/brush-layer';
import { parseStrokeDasharray } from '@meitu/saas-editor-core/es/utils/file-kit';
import { IBrushLayerProps } from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useGlobalWidget } from '@meitu/saas-editor-ui/es/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';

const { Option } = Select;

export const StyleFeature: React.FC<{
    id?: string;
    dasharray?: string;
    linecap?: string;
    isStroke: boolean;
    onAfterChange?: Function;
    selectWidth?: number;
    style?: React.CSSProperties;
}> = React.memo(({ id, dasharray, linecap, isStroke, onAfterChange, selectWidth = 174, style }) => {
    const { $t } = useLocaleStore();
    const selectGlobalWidgetRef = useGlobalWidget(editor, false);
    const needListener = !isUndefined(id);
    const res = useListenActivePickLayerProps(
        editor,
        (layer) => {
            if (!layer) return null;
            const { fills, stroke } = layer.data as IBrushLayerProps;
            if (isStroke) {
                return {
                    dasharray: stroke.dasharray,
                    linecap: stroke.linecap,
                };
            }
            return {
                dasharray: fills[0].dasharray,
                linecap: fills[0].linecap,
            };
        },
        true,
        needListener,
    );

    if (needListener) {
        if (isNull(res)) {
            return null;
        }
        dasharray = res.dasharray;
        linecap = res.linecap;
    } else {
        if (isUndefined(dasharray) || isUndefined(linecap)) return null;
    }

    return (
        <div className="c-brush-panel__item" style={{ ...style }}>
            <LabelAction title={$t((v) => v.panel.brushPanel.style)} align={true}>
                <div style={{ width: selectWidth }}>
                    <Select
                        onDropdownVisibleChange={(e) => {
                            selectGlobalWidgetRef.current = e;
                        }}
                        value={`${dasharray}-${linecap}`}
                        className="c-brush-panel__dasharray"
                        suffixIcon={
                            <IconFont name="ChevronDownBlack" fontSize={10} color="var(--content-input-label)" />
                        }
                        onChange={(value: string, option: any) => {
                            const { linecap, dasharray } = option;
                            if (id) {
                                editor.brushLayer.setStyle(id, dasharray, linecap, isStroke);
                            }
                            onAfterChange?.(isStroke ? 19 : 77, {
                                linecap,
                                dasharray,
                            });
                        }}
                    >
                        {BRUSH_DASHARRAY_OPTIONS.map((item, index) => {
                            return (
                                <Option
                                    key={index}
                                    linecap={item.linecap}
                                    dasharray={item.value}
                                    value={`${item.value}-${item.linecap}`}
                                >
                                    <div
                                        style={{
                                            height: 18,
                                            width: 162,
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <svg
                                            width="150"
                                            height="26"
                                            viewBox="0 0 150 26"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fill="none"
                                                d="M13 13 L140 13"
                                                stroke="var(--content-system-tertiary)"
                                                strokeWidth={2}
                                                strokeDasharray={parseStrokeDasharray(item.value, 2, true) as string}
                                                strokeLinecap={item.linecap as any}
                                            />
                                        </svg>
                                    </div>
                                </Option>
                            );
                        })}
                    </Select>
                </div>
            </LabelAction>
        </div>
    );
});
